export const SQL_RESERVED_WORDS = [
    'all',
    'and',
    'as',
    'asc',
    'avg',
    'between',
    'by',
    'case',
    'count',
    'cross',
    'desc',
    'distinct',
    'else',
    'end',
    'exists',
    'from',
    'full',
    'group',
    'having',
    'inner',
    'is',
    'join',
    'left',
    'limit',
    'max',
    'min',
    'not',
    'null',
    'offset',
    'on',
    'or',
    'order',
    'right',
    'select',
    'sum',
    'then',
    'true',
    'false',
    'union',
    'when',
    'where',
    'with',
];

const SQL_INDENT = '    ';
const PROTECTED_SQL_SEGMENT_PATTERN = /({{[\s\S]*?}}|--[^\n]*|\/\*[\s\S]*?\*\/|'(?:''|[^'])*'|"(?:""|[^"])*")/g;
const SQL_LINE_BREAK_TOKEN = '__VANTAGE_SQL_LINE_BREAK__';
const SQL_MAJOR_CLAUSES = [
    'UNION ALL',
    'UNION',
    'SELECT',
    'FROM',
    'WHERE',
    'GROUP BY',
    'HAVING',
    'ORDER BY',
    'LIMIT',
    'OFFSET',
    'LEFT JOIN',
    'RIGHT JOIN',
    'FULL JOIN',
    'INNER JOIN',
    'CROSS JOIN',
    'JOIN',
];

const countCharacter = (value: string, character: string) =>
    [...value].filter((candidate) => candidate === character).length;

const splitTopLevelCommaList = (value: string) => {
    const parts: string[] = [];
    let start = 0;
    let depth = 0;

    for (let index = 0; index < value.length; index += 1) {
        const character = value[index];

        if (character === '(') {
            depth += 1;
        } else if (character === ')') {
            depth = Math.max(0, depth - 1);
        } else if (character === ',' && depth === 0) {
            parts.push(value.slice(start, index).trim());
            start = index + 1;
        }
    }

    parts.push(value.slice(start).trim());

    return parts.filter(Boolean);
};

export const formatSqlQuery = (sql: string) => {
    const protectedSegments: string[] = [];
    const maskedSql = sql.replace(PROTECTED_SQL_SEGMENT_PATTERN, (segment) => {
        const token = `__VANTAGE_SQL_PROTECTED_${protectedSegments.length}__`;
        protectedSegments.push(segment);

        return segment.startsWith('--') ? `${token} ${SQL_LINE_BREAK_TOKEN}` : token;
    });
    const keywordPattern = new RegExp(`\\b(${SQL_RESERVED_WORDS.join('|')})\\b`, 'gi');
    const majorClausePattern = new RegExp(`\\s+\\b(${SQL_MAJOR_CLAUSES.join('|').replace(/ /g, '\\s+')})\\b`, 'gi');
    const normalizedSql = maskedSql
        .replace(/\s+/g, ' ')
        .trim()
        .replace(keywordPattern, (match) => match.toUpperCase())
        .replace(/\(\s*(SELECT|WITH)\b/gi, '(\n$1')
        .replace(majorClausePattern, '\n$1')
        .replace(new RegExp(`\\s*${SQL_LINE_BREAK_TOKEN}\\s*`, 'g'), '\n');
    const lines = normalizedSql.split('\n');
    const formattedLines: string[] = [];
    let depth = 0;

    lines.forEach((rawLine) => {
        const line = rawLine.trim();

        if (!line) {
            return;
        }

        const leadingClosingParentheses = line.match(/^\)+/)?.[0].length ?? 0;
        const lineDepth = Math.max(0, depth - leadingClosingParentheses);
        const selectMatch = line.match(/^SELECT\s+(.+)$/i);

        if (selectMatch) {
            const fields = splitTopLevelCommaList(selectMatch[1]);
            formattedLines.push(`${SQL_INDENT.repeat(lineDepth)}SELECT`);
            fields.forEach((field, index) => {
                const suffix = index < fields.length - 1 ? ',' : '';
                formattedLines.push(`${SQL_INDENT.repeat(lineDepth + 1)}${field}${suffix}`);
            });
        } else {
            formattedLines.push(`${SQL_INDENT.repeat(lineDepth)}${line}`);
        }

        depth = Math.max(0, depth + countCharacter(line, '(') - countCharacter(line, ')'));
    });

    return formattedLines
        .join('\n')
        .replace(/__VANTAGE_SQL_PROTECTED_(\d+)__/g, (_token, index: string) => protectedSegments[Number(index)]);
};

export const formatSqlKeywords = formatSqlQuery;

export const getClientSqlDiagnostic = (sql: string) => {
    if (/(^|[^=])={3}($|[^=])/.test(sql)) {
        return 'Use SQL equality operator "=" instead of JavaScript "===".';
    }

    if (/(^|[^!])={2}($|[^=])/.test(sql)) {
        return 'Use SQL equality operator "=" instead of JavaScript "==".';
    }

    return undefined;
};
